import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getLineActivityLabelling, getWorkorderActivityLabelling, setUserNumberOfCopiesPerPallet } from "../../aplApi/activitiesHelper";
import { getPrintersOffline } from "../../aplApi/printersHelper";
import { OfflinePrintersSearchProps } from "../../types/offlinePrintersSearchProps";
import { ReduxStateLabelling } from "../../types/redux/reduxStateLabelling";
import { initialStateLabelling } from "../defaultState";
import { getActiveLabels } from "../../aplApi/labelsHelper";

export const getLabellingByLineId = createAsyncThunk("labelling/getLabellingByLineId", async (lineId: number) => {
    let response = await getLineActivityLabelling(lineId);
    return response.data;
});

export const getLabellingByWorkorderId = createAsyncThunk("labelling/getLabellingByWorkorderId", async (props: { operationId: string }) => {
    let response = await getWorkorderActivityLabelling(props.operationId);
    return response.data;
});

export const getPrintersOfflineByLine = createAsyncThunk(
    "labelling/getPrintersOfflineByLine",
    async (props: OfflinePrintersSearchProps) => {
        let response = await getPrintersOffline(props);
        return response.data;
    }
);

export const getLabelsForReprint = createAsyncThunk("labelling/getLabelsForReprint", async () => {
    let response = await getActiveLabels();
    return response.data;
});

export const setUserNumberOfCopiesPerPalletRedux = createAsyncThunk(
    "labelling/setUserNumberOfCopiesPerPallet",
    async (props: { operationId: string; value: number }) => {
        let response = await setUserNumberOfCopiesPerPallet(props.operationId, props.value);
        return response.data;
    }
);

const labelling = createSlice({
    name: "labelling",
    initialState: initialStateLabelling,
    reducers: {
        onClearLabellingActivities: (): ReduxStateLabelling => ({ ...initialStateLabelling }),
        onClearOfflinePrinters: (state): ReduxStateLabelling => ({ ...state, offlinePrinters: [], lastUpdated: new Date() }),
        onClearLabelsForReprint: (state): ReduxStateLabelling => ({ ...state, labels: [], lastUpdated: new Date() }),
        onSetOffline: (state, action): ReduxStateLabelling => ({ ...state, offline: action.payload, lastUpdated: new Date() }),
        onSetOfflineTimeStamp: (state, action): ReduxStateLabelling => ({
            ...state,
            offlineTimeStamp: action.payload,
            lastUpdated: new Date()
        }),
        onSetQtyCount: (state, action) => ({ ...state, selectedQtyCount: action.payload, lastUpdated: new Date() }),
        onSetCopiesCount: (state, action): ReduxStateLabelling => ({
            ...state,
            selectedCopiesCount: action.payload,
            lastUpdated: new Date()
        }),
        onSetPalletCount: (state, action): ReduxStateLabelling => ({
            ...state,
            selectedPalletCount: action.payload,
            lastUpdated: new Date()
        }),
        onSetPrinterId: (state, action): ReduxStateLabelling => ({ ...state, selectedPrinterId: action.payload, lastUpdated: new Date() }),
        onSetLabelId: (state, action): ReduxStateLabelling => ({ ...state, selectedLabelId: action.payload, lastUpdated: new Date() }),
        onSetPrintingProgress: (state, action): ReduxStateLabelling => ({
            ...state,
            printingProgress: action.payload,
            lastUpdated: new Date()
        }),
        onAddPrinted: (state, action): ReduxStateLabelling => ({
            ...state,
            printed: [...state.printed, ...action.payload],
            lastUpdated: new Date()
        }),
        onClearPrinted: (state): ReduxStateLabelling => ({ ...state, printed: [], lastUpdated: new Date() }),
        onSetCombinedPdf: (state, action): ReduxStateLabelling => ({ ...state, combinedPdf: action.payload, lastUpdated: new Date() }),
        onSetCopesPerPalletChanged: (state, action): ReduxStateLabelling => ({
            ...state,
            userNumberOfCopiesPerPalletChanged: action.payload,
            lastUpdated: new Date()
        })
    },
    extraReducers: builder => {
        builder
            .addCase(getLabellingByLineId.pending, state => {
                return {
                    ...state,
                    activitiesLoading: true,
                    activitiesErrorMessage: ""
                } as ReduxStateLabelling;
            })
            .addCase(getLabellingByLineId.fulfilled, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: "",
                    activities: action.payload,
                    userNumberOfCopiesPerPalletChanged: false,
                    lastUpdated: new Date()
                } as ReduxStateLabelling;
            })
            .addCase(getLabellingByLineId.rejected, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: action.error.message ?? "API error",
                    activities: [],
                    userNumberOfCopiesPerPalletChanged: false
                } as ReduxStateLabelling;
            });

        builder
            .addCase(getLabellingByWorkorderId.pending, state => {
                return {
                    ...state,
                    activitiesLoading: true,
                    activitiesErrorMessage: ""
                } as ReduxStateLabelling;
            })
            .addCase(getLabellingByWorkorderId.fulfilled, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: "",
                    activities: action.payload,
                    userNumberOfCopiesPerPalletChanged: false,
                    lastUpdated: new Date()
                } as ReduxStateLabelling;
            })
            .addCase(getLabellingByWorkorderId.rejected, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: action.error.message ?? "API error",
                    activities: [],
                    userNumberOfCopiesPerPalletChanged: false
                } as ReduxStateLabelling;
            });

        builder
            .addCase(getPrintersOfflineByLine.pending, state => {
                return {
                    ...state,
                    printersLoading: true,
                    printersErrorMessage: ""
                } as ReduxStateLabelling;
            })
            .addCase(getPrintersOfflineByLine.fulfilled, (state, action) => {
                return {
                    ...state,
                    printersLoading: false,
                    printersErrorMessage: "",
                    offlinePrinters: action.payload,
                    lastUpdated: new Date()
                } as ReduxStateLabelling;
            })
            .addCase(getPrintersOfflineByLine.rejected, (state, action) => {
                return {
                    ...state,
                    printersLoading: false,
                    printersErrorMessage: action.error.message ?? "API error",
                    printers: [],
                    selectedPrinterId: -1
                } as ReduxStateLabelling;
            });

        builder
            .addCase(getLabelsForReprint.pending, state => {
                return {
                    ...state,
                    labelsLoading: true
                } as ReduxStateLabelling;
            })
            .addCase(getLabelsForReprint.fulfilled, (state, action) => {
                return {
                    ...state,
                    labelsLoading: false,
                    labels: action.payload,
                    lastUpdated: new Date()
                } as ReduxStateLabelling;
            })
            .addCase(getLabelsForReprint.rejected, (state, action) => {
                return {
                    ...state,
                    labelsLoading: false,
                    labelsErrorMessage: action.error.message ?? "API error",
                    labels: []
                } as ReduxStateLabelling;
            });

        builder
            .addCase(setUserNumberOfCopiesPerPalletRedux.pending, state => {
                return {
                    ...state,
                    activitiesLoading: true,
                    activitiesErrorMessage: ""
                } as ReduxStateLabelling;
            })
            .addCase(setUserNumberOfCopiesPerPalletRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: "",
                    activities: action.payload,
                    userNumberOfCopiesPerPalletChanged: false,
                    lastUpdated: new Date()
                } as ReduxStateLabelling;
            })
            .addCase(setUserNumberOfCopiesPerPalletRedux.rejected, (state, action) => {
                return {
                    ...state,
                    activitiesLoading: false,
                    activitiesErrorMessage: action.error.message ?? "API error",
                    activities: [],
                    userNumberOfCopiesPerPalletChanged: false
                } as ReduxStateLabelling;
            });
    }
});

export const {
    onClearLabellingActivities,
    onClearOfflinePrinters,
    onClearLabelsForReprint,
    onSetOffline,
    onSetOfflineTimeStamp,
    onSetCopiesCount,
    onSetQtyCount,
    onSetPalletCount,
    onSetPrinterId,
    onSetPrintingProgress,
    onSetLabelId,
    onAddPrinted,
    onClearPrinted,
    onSetCombinedPdf,
    onSetCopesPerPalletChanged
} = labelling.actions;
export default labelling.reducer;
